import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import { Plus, Pencil, Trash2, X, Layers, MapPin, Check, Building2, ChevronDown, ChevronRight, Eye } from 'lucide-react';
import OnizlemeModal from '../components/OnizlemeModal';

/* ── Genel Modal ── */
function Modal({ title, onClose, wide, children }) {
  return (
    <div className="modal-overlay">
      <div className={`modal ${wide ? 'modal-lg' : ''}`}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── Soru Tipleri ── */
const SORU_TIPLERI = [
  { value: 'yildiz',        label: '⭐ Yıldız (1-5)' },
  { value: 'emoji',         label: '😊 Emoji Derecelendirme' },
  { value: 'evet_hayir',    label: '✅ Evet/Hayır' },
  { value: 'cok_secenekli', label: '☑️ Çoktan Seçmeli' },
  { value: 'acik_metin',    label: '📝 Açık Metin' },
];
const tipiLabel = t => SORU_TIPLERI.find(x => x.value === t)?.label || t;
const EMOJILER = ['😡','😞','😐','😊','😍'];
const EMOJI_LABELS = ['Çok Kötü','Kötü','Orta','İyi','Mükemmel'];
const BOSH_SORU = { soru_metni: '', soru_tipi: 'yildiz', zorunlu: true, secenekler: [] };

/* ── Seçenek Editörü ── */
function SecenekEditor({ secenekler, onChange }) {
  const refs = useRef([]);
  const add = () => { const y = [...secenekler, '']; onChange(y); setTimeout(() => refs.current[y.length-1]?.focus(), 30); };
  const upd = (i, v) => onChange(secenekler.map((s,j) => j===i ? v : s));
  const del = (i) => onChange(secenekler.filter((_,j) => j!==i));
  const onKey = (e,i) => { if(e.key==='Enter'){e.preventDefault();e.stopPropagation(); i===secenekler.length-1 ? add() : refs.current[i+1]?.focus();} };
  return (
    <div style={{marginTop:8}}>
      <div style={{fontSize:12,fontWeight:600,color:'var(--gray-600)',marginBottom:6}}>Seçenekler</div>
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        {secenekler.map((s,i) => (
          <div key={i} style={{display:'flex',gap:6,alignItems:'center'}}>
            <span style={{fontSize:12,color:'var(--gray-400)',minWidth:18}}>{i+1}.</span>
            <input ref={el=>refs.current[i]=el} className="input" style={{flex:1,padding:'7px 10px',fontSize:13}}
              placeholder={`Seçenek ${i+1}...`} value={s} onChange={e=>upd(i,e.target.value)} onKeyDown={e=>onKey(e,i)}/>
            <button type="button" className="btn btn-danger btn-icon btn-sm" onClick={()=>del(i)} tabIndex={-1}><X size={12}/></button>
          </div>
        ))}
        {!secenekler.length && <p style={{fontSize:12,color:'var(--gray-400)',margin:0}}>Henüz seçenek yok</p>}
      </div>
      <button type="button" className="btn btn-secondary btn-sm" style={{marginTop:8}} onClick={add}><Plus size={12}/> Seçenek Ekle</button>
    </div>
  );
}

/* ── Soru Satırı ── */
function SoruSatir({s,i,onSil,onEdit}) {
  return (
    <div style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',background:'var(--gray-50)',borderRadius:10,border:'1px solid var(--gray-100)',marginBottom:6}}>
      <span style={{fontSize:12,fontWeight:700,color:'var(--gray-400)',minWidth:20,paddingTop:2}}>{i+1}</span>
      <div style={{flex:1}}>
        <div style={{fontSize:13,fontWeight:500}}>{s.soru_metni}</div>
        <div style={{fontSize:11,color:'var(--gray-400)',marginTop:2}}>
          {tipiLabel(s.soru_tipi)} {s.zorunlu ? '· Zorunlu' : ''}
          {s.soru_tipi==='cok_secenekli'&&s.secenekler?.length>0&&
            <span style={{color:'var(--blue)',marginLeft:6}}>({s.secenekler.slice(0,3).join(', ')}{s.secenekler.length>3?'…':''})</span>}
        </div>
      </div>
      <div style={{display:'flex',gap:4}}>
        {onEdit&&<button className="btn btn-secondary btn-sm btn-icon" type="button" onClick={()=>onEdit(s,i)}><Pencil size={12}/></button>}
        {onSil&&<button className="btn btn-danger btn-sm btn-icon" type="button" onClick={()=>onSil(s,i)}><Trash2 size={12}/></button>}
      </div>
    </div>
  );
}

/* ── Soru Formu ── */
function SoruForm({value,onChange,onSubmit,loading,label='Soru Ekle',onCancel}) {
  const ref = useRef(null);
  useEffect(()=>{ref.current?.focus();},[]);
  const set = (k,v) => onChange({...value,[k]:v});
  return (
    <div style={{background:'var(--gray-50)',borderRadius:10,padding:14,display:'flex',flexDirection:'column',gap:10}}>
      <div>
        <label style={{fontSize:12,fontWeight:600,color:'var(--gray-600)',display:'block',marginBottom:4}}>Soru Metni *</label>
        <input ref={ref} className="input" placeholder="Soruyu yazın..." value={value.soru_metni}
          onChange={e=>set('soru_metni',e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter'&&value.soru_tipi!=='cok_secenekli'){e.preventDefault();onSubmit();}}}/>
      </div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
        <select className="input" style={{flex:1,minWidth:180}} value={value.soru_tipi} onChange={e=>set('soru_tipi',e.target.value)}>
          {SORU_TIPLERI.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer',whiteSpace:'nowrap'}}>
          <input type="checkbox" checked={value.zorunlu} onChange={e=>set('zorunlu',e.target.checked)}/> Zorunlu
        </label>
      </div>
      {value.soru_tipi==='cok_secenekli'&&<SecenekEditor secenekler={value.secenekler||[]} onChange={v=>set('secenekler',v)}/>}
      {value.soru_tipi==='emoji'&&(
        <div style={{display:'flex',gap:10,justifyContent:'center',padding:'6px 0'}}>
          {EMOJILER.map((e,i)=>(
            <div key={i} style={{textAlign:'center'}}>
              <div style={{fontSize:26}}>{e}</div>
              <div style={{fontSize:10,color:'var(--gray-400)'}}>{EMOJI_LABELS[i]}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{display:'flex',gap:8}}>
        {onCancel&&<button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>İptal</button>}
        <button type="button" className="btn btn-secondary btn-sm" onClick={onSubmit} disabled={loading} style={{flex:1}}>
          <Plus size={14}/> {label}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   LOKASYON ATA MODAL
   tip: 'anket' | 'checklist'
══════════════════════════════════════════════════════════════ */
function LokasyonAtaModal({ sablon, tip, onClose, onAtandi }) {
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [secili, setSecili] = useState(new Set());
  const [acikKatlar, setAcikKatlar] = useState({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const endpoint = tip === 'anket'
          ? `/anketler/${sablon.id}/atanabilir-lokasyonlar`
          : `/checklistler/${sablon.id}/atanabilir-lokasyonlar`;
        const { data } = await api.get(endpoint);
        setLokasyonlar(data);
        // Tüm katları açık başlat
        const katlar = {};
        data.forEach(l => { const k = l.kat_adi || '__'; katlar[k] = true; });
        setAcikKatlar(katlar);
      } catch { toast.error('Lokasyonlar yüklenemedi'); }
      finally { setLoading(false); }
    };
    load();
  }, [sablon.id, tip]);

  // Kat → lokasyon grupla
  const groups = {};
  lokasyonlar.forEach(l => {
    const bina = l.bina_adi || '(Bina yok)';
    const kat  = l.kat_adi  || null;
    if (!groups[bina]) groups[bina] = {};
    const k = kat || '__';
    if (!groups[bina][k]) groups[bina][k] = [];
    groups[bina][k].push(l);
  });

  const toggleLok = (id) => setSecili(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const toggleKat = (bina, kat) => {
    const loks = groups[bina][kat] || [];
    const hepsiSecili = loks.every(l => secili.has(l.id));
    setSecili(prev => {
      const s = new Set(prev);
      loks.forEach(l => hepsiSecili ? s.delete(l.id) : s.add(l.id));
      return s;
    });
  };

  const tumunuSec = () => {
    if (secili.size === lokasyonlar.length) setSecili(new Set());
    else setSecili(new Set(lokasyonlar.map(l => l.id)));
  };

  const handleAta = async () => {
    if (!secili.size) return toast.error('En az bir lokasyon seçin');
    setSaving(true);
    try {
      const endpoint = tip === 'anket'
        ? `/anketler/${sablon.id}/toplu-ata`
        : `/checklistler/${sablon.id}/toplu-ata`;
      await api.post(endpoint, { lokasyon_ids: [...secili] });
      toast.success(`${secili.size} lokasyona atandı`);
      onAtandi();
      onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Hata'); }
    finally { setSaving(false); }
  };

  const toggleAcikKat = (key) => setAcikKatlar(prev => ({...prev, [key]: !prev[key]}));

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div className="modal-title">
              <MapPin size={14} style={{marginRight:6,verticalAlign:'middle',color:'var(--blue)'}}/>
              Lokasyona Ata: <span style={{color:'var(--blue)'}}>{sablon.baslik}</span>
            </div>
            <div style={{fontSize:11,color:'var(--gray-400)',marginTop:2}}>
              Daha önce {tip === 'anket' ? 'anket' : 'checklist'} atanmış lokasyonlar listede görünmez
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16}/></button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div style={{display:'flex',justifyContent:'center',padding:40}}>
              <div className="spinner spinner-blue" style={{width:28,height:28}}/>
            </div>
          ) : lokasyonlar.length === 0 ? (
            <div style={{textAlign:'center',padding:'40px 20px',color:'var(--gray-400)'}}>
              <MapPin size={32} style={{marginBottom:10,opacity:0.3}}/>
              <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>Atanabilir lokasyon yok</div>
              <div style={{fontSize:12}}>Tüm aktif lokasyonlara bu {tip === 'anket' ? 'anket' : 'checklist'} zaten atanmış.</div>
            </div>
          ) : (
            <>
              {/* Üst araç çubuğu */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,padding:'8px 12px',background:'var(--gray-50)',borderRadius:10}}>
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,fontWeight:500}}>
                  <input type="checkbox"
                    checked={secili.size === lokasyonlar.length && lokasyonlar.length > 0}
                    onChange={tumunuSec}/>
                  Tümünü Seç ({lokasyonlar.length} lokasyon)
                </label>
                <span className="badge badge-blue">{secili.size} seçili</span>
              </div>

              {/* Bina → Kat → Lokasyon listesi */}
              <div style={{maxHeight:400,overflowY:'auto',display:'flex',flexDirection:'column',gap:12}}>
                {Object.entries(groups).map(([bina, katGruplari]) => (
                  <div key={bina}>
                    {/* Bina başlığı */}
                    <div style={{display:'flex',alignItems:'center',gap:8,padding:'6px 4px',marginBottom:6}}>
                      <Building2 size={13} color="var(--blue)"/>
                      <span style={{fontWeight:700,fontSize:13}}>{bina}</span>
                      <span className="badge badge-gray" style={{fontSize:10}}>
                        {Object.values(katGruplari).flat().length}
                      </span>
                    </div>

                    {Object.entries(katGruplari).map(([katKey, loks]) => {
                      const katAdi = katKey === '__' ? null : katKey;
                      const acikKey = bina + '|' + katKey;
                      const acik = acikKatlar[acikKey] !== false;
                      const hepsiSecili = loks.every(l => secili.has(l.id));
                      const bazıSecili = loks.some(l => secili.has(l.id)) && !hepsiSecili;

                      return (
                        <div key={katKey} style={{marginLeft:12,marginBottom:6}}>
                          {/* Kat başlığı */}
                          {katAdi && (
                            <div style={{display:'flex',alignItems:'center',gap:8,padding:'5px 8px',background:'#f0f7ff',borderRadius:8,marginBottom:4,cursor:'pointer'}}
                              onClick={() => toggleAcikKat(acikKey)}>
                              <input type="checkbox"
                                checked={hepsiSecili}
                                ref={el => { if(el) el.indeterminate = bazıSecili; }}
                                onChange={() => toggleKat(bina, katKey)}
                                onClick={e => e.stopPropagation()}/>
                              <Layers size={12} color="var(--blue)"/>
                              <span style={{fontSize:12,fontWeight:600,color:'var(--blue)',flex:1}}>{katAdi}</span>
                              <span className="badge badge-gray" style={{fontSize:10}}>{loks.length}</span>
                              {acik ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
                            </div>
                          )}

                          {/* Lokasyonlar */}
                          {(!katAdi || acik) && (
                            <div style={{display:'flex',flexDirection:'column',gap:4,marginLeft: katAdi ? 8 : 0}}>
                              {loks.map(l => (
                                <label key={l.id}
                                  style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',
                                    background: secili.has(l.id) ? '#eff6ff' : '#fff',
                                    border: `1px solid ${secili.has(l.id) ? '#bfdbfe' : 'var(--gray-100)'}`,
                                    borderRadius:8,cursor:'pointer',transition:'all 0.1s'}}>
                                  <input type="checkbox"
                                    checked={secili.has(l.id)}
                                    onChange={() => toggleLok(l.id)}/>
                                  <MapPin size={11} color={secili.has(l.id) ? 'var(--blue)' : 'var(--gray-400)'}/>
                                  <div style={{flex:1}}>
                                    <div style={{fontSize:13,fontWeight:500}}>{l.ad}</div>
                                    {l.alan && <div style={{fontSize:11,color:'var(--gray-400)'}}>{l.alan}</div>}
                                  </div>
                                  {secili.has(l.id) && <Check size={14} color="var(--blue)"/>}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>İptal</button>
          {lokasyonlar.length > 0 && (
            <button className="btn btn-primary" onClick={handleAta} disabled={saving || !secili.size}>
              {saving ? <div className="spinner"/> : <MapPin size={14}/>}
              {secili.size > 0 ? `${secili.size} Lokasyona Ata` : 'Lokasyon Seç'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ANA COMPONENT
══════════════════════════════════════════════════════════════ */
export default function Anketler() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [kurumlar, setKurumlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [modalSorular, setModalSorular] = useState([]);
  const [detayId, setDetayId] = useState(null);
  const [detay, setDetay] = useState(null);
  const [form, setForm] = useState({ baslik:'', aciklama:'', kurum_id:'', sorular:[] });
  const [saving, setSaving] = useState(false);
  const [yeniSoru, setYeniSoru] = useState({...BOSH_SORU});
  const [soruEkleniyor, setSoruEkleniyor] = useState(false);
  const [editSoru, setEditSoru] = useState(null);
  const [ataModal, setAtaModal] = useState(null);
  const [onizleme, setOnizleme] = useState(null); // {sablon, sorular}

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [api.get('/anketler')];
      if (user.rol === 'superadmin') reqs.push(api.get('/kurumlar'));
      const [a, k] = await Promise.all(reqs);
      setList(a.data); if (k) setKurumlar(k.data);
    } catch { toast.error('Yüklenemedi'); } finally { setLoading(false); }
  };

  const loadDetay = async id => {
    try { const {data} = await api.get(`/anketler/${id}`); setDetay(data); }
    catch { toast.error('Detay yüklenemedi'); }
  };

  useEffect(()=>{load();},[]);
  useEffect(()=>{ if(detayId) loadDetay(detayId); else setDetay(null); },[detayId]);

  const openEkle = () => {
    setForm({baslik:'',aciklama:'',kurum_id:user.rol==='superadmin'?'':user.kurum_id,sorular:[]});
    setYeniSoru({...BOSH_SORU}); setModalSorular([]); setEditSoru(null); setModal('ekle');
  };
  const openDuzenle = async a => {
    setForm({baslik:a.baslik,aciklama:a.aciklama||'',kurum_id:a.kurum_id,sorular:[]});
    setYeniSoru({...BOSH_SORU}); setEditSoru(null); setModal(a);
    try { const {data}=await api.get(`/anketler/${a.id}`); setModalSorular(data.sorular||[]); }
    catch { setModalSorular([]); }
  };

  const addSoruToForm = () => {
    if (!yeniSoru.soru_metni.trim()) return toast.error('Soru metni gerekli');
    if (yeniSoru.soru_tipi==='cok_secenekli'&&(yeniSoru.secenekler||[]).filter(Boolean).length<2) return toast.error('En az 2 seçenek');
    setForm(f=>({...f,sorular:[...f.sorular,{...yeniSoru}]})); setYeniSoru({...BOSH_SORU});
  };
  const removeSoruFromForm = (_,i) => setForm(f=>({...f,sorular:f.sorular.filter((_,j)=>j!==i)}));
  const editSoruInForm = (s,i) => setEditSoru({soru:{...s},index:i,mode:'form'});
  const saveEditForm = () => {
    if (!editSoru.soru.soru_metni.trim()) return toast.error('Soru metni gerekli');
    setForm(f=>({...f,sorular:f.sorular.map((s,i)=>i===editSoru.index?{...editSoru.soru}:s)})); setEditSoru(null);
  };
  const handleSoruEkleDB = async (anketId) => {
    if (!yeniSoru.soru_metni.trim()) return toast.error('Soru metni gerekli');
    if (yeniSoru.soru_tipi==='cok_secenekli'&&(yeniSoru.secenekler||[]).filter(Boolean).length<2) return toast.error('En az 2 seçenek');
    setSoruEkleniyor(true);
    try {
      await api.post(`/anketler/${anketId}/sorular`,{...yeniSoru,secenekler:(yeniSoru.secenekler||[]).filter(Boolean)});
      toast.success('Soru eklendi'); setYeniSoru({...BOSH_SORU});
      const {data}=await api.get(`/anketler/${anketId}`); setModalSorular(data.sorular||[]);
      if(detayId===anketId) loadDetay(anketId);
    } catch { toast.error('Hata'); } finally { setSoruEkleniyor(false); }
  };
  const handleSoruSilDB = async (anketId,soruId) => {
    if (!window.confirm('Soruyu silmek istiyor musunuz?')) return;
    try { await api.delete(`/anketler/sorular/${soruId}`); setModalSorular(prev=>prev.filter(s=>s.id!==soruId)); if(detayId===anketId) loadDetay(anketId); }
    catch { toast.error('Silinemedi'); }
  };
  const handleSoruGuncelleDB = async (anketId,soruId,veri) => {
    try {
      await api.put(`/anketler/sorular/${soruId}`,{...veri,secenekler:(veri.secenekler||[]).filter(Boolean)});
      toast.success('Soru güncellendi'); setEditSoru(null);
      const {data}=await api.get(`/anketler/${anketId}`); setModalSorular(data.sorular||[]);
      if(detayId===anketId) loadDetay(anketId);
    } catch { toast.error('Güncellenemedi'); }
  };
  const editSoruDB = (s) => setEditSoru({soru:{soru_metni:s.soru_metni,soru_tipi:s.soru_tipi,zorunlu:s.zorunlu,secenekler:s.secenekler||[],id:s.id},mode:'db'});
  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if(modal==='ekle') { await api.post('/anketler',{...form,sorular:form.sorular.map(s=>({...s,secenekler:(s.secenekler||[]).filter(Boolean)}))}); toast.success('Anket oluşturuldu'); }
      else { await api.put(`/anketler/${modal.id}`,{baslik:form.baslik,aciklama:form.aciklama,aktif:true}); toast.success('Anket güncellendi'); }
      setModal(null); load();
    } catch(err) { toast.error(err.response?.data?.error||'Hata'); } finally { setSaving(false); }
  };
  const handleDelete = async a => {
    if(!window.confirm(`"${a.baslik}" anketini silmek istiyor musunuz?`)) return;
    try { await api.delete(`/anketler/${a.id}`); toast.success('Silindi'); load(); if(detayId===a.id) setDetayId(null); }
    catch(err) { toast.error(err.response?.data?.error||'Silinemedi'); }
  };

  const renderSorular = (sorular, anketId, mode) => (
    <>
      {sorular.map((s,i)=>(
        <SoruSatir key={s.id||i} s={s} i={i}
          onSil={mode==='form'?(_,idx)=>removeSoruFromForm(_,idx):(s)=>handleSoruSilDB(anketId,s.id)}
          onEdit={mode==='form'?editSoruInForm:(s)=>editSoruDB(s)}/>
      ))}
      {!sorular.length&&<p style={{color:'var(--gray-400)',fontSize:13,marginBottom:10}}>Henüz soru yok</p>}
      {editSoru&&(
        <div style={{marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:6,color:'var(--navy)'}}>✏️ Soruyu Düzenle</div>
          <SoruForm value={editSoru.soru} onChange={s=>setEditSoru(prev=>({...prev,soru:s}))}
            onSubmit={mode==='form'?saveEditForm:()=>handleSoruGuncelleDB(anketId,editSoru.soru.id,editSoru.soru)}
            label="Güncelle" onCancel={()=>setEditSoru(null)}/>
        </div>
      )}
      {!editSoru&&<SoruForm value={yeniSoru} onChange={setYeniSoru}
        onSubmit={mode==='form'?addSoruToForm:()=>handleSoruEkleDB(anketId)} loading={soruEkleniyor}/>}
    </>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Anket Şablonları</h1>
          <p className="page-subtitle">{list.length} anket şablonu mevcut</p>
        </div>
        <button className="btn btn-primary" onClick={openEkle}><Plus size={16}/> Yeni Anket</button>
      </div>

      <div className={detayId ? 'chart-grid' : ''} style={{display:'grid',gap:20}}>
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Anket Adı</th>
                {user.rol==='superadmin'&&<th>Kurum</th>}
                <th>Soru</th><th>Bağlı Lok.</th><th>Durum</th><th></th>
              </tr></thead>
              <tbody>
                {list.map(a=>(
                  <tr key={a.id} style={{cursor:'pointer',background:detayId===a.id?'#eff6ff':''}}>
                    <td onClick={()=>setDetayId(detayId===a.id?null:a.id)}>
                      <div style={{fontWeight:600}}>{a.baslik}</div>
                      {a.aciklama&&<div style={{fontSize:12,color:'var(--gray-400)'}}>{a.aciklama}</div>}
                    </td>
                    {user.rol==='superadmin'&&<td onClick={()=>setDetayId(detayId===a.id?null:a.id)}>{a.kurum_adi||'—'}</td>}
                    <td onClick={()=>setDetayId(detayId===a.id?null:a.id)}><span className="badge badge-blue">{a.soru_sayisi}</span></td>
                    <td onClick={()=>setDetayId(detayId===a.id?null:a.id)}><span className="badge badge-gray">{a.bagli_lokasyon}</span></td>
                    <td><span className={`badge ${a.aktif?'badge-green':'badge-gray'}`}>{a.aktif?'Aktif':'Pasif'}</span></td>
                    <td>
                      <div style={{display:'flex',gap:5}}>
                        <button className="btn btn-secondary btn-sm btn-icon" title="Önizle"
                          onClick={async ()=>{ try{ const {data}=await api.get(`/anketler/${a.id}`); setOnizleme({...a,sorular:data.sorular||[]}); }catch{} }}>
                          <Eye size={13}/>
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={()=>setAtaModal(a)} title="Lokasyona Ata">
                          <MapPin size={13}/> Ata
                        </button>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={()=>openDuzenle(a)}><Pencil size={13}/></button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={()=>handleDelete(a)}><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!list.length&&!loading&&<tr><td colSpan={6} style={{textAlign:'center',padding:40,color:'var(--gray-400)'}}>Henüz anket yok</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {detayId&&detay&&(
          <div className="card" style={{padding:20}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
              <h3 style={{margin:0,fontSize:15,fontWeight:700}}>{detay.anket?.baslik}</h3>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>setDetayId(null)}><X size={15}/></button>
            </div>
            <h4 style={{fontSize:13,fontWeight:600,color:'var(--gray-600)',marginBottom:10}}>Sorular ({detay.sorular?.length})</h4>
            {renderSorular(detay.sorular||[],detayId,'db')}
          </div>
        )}
      </div>

      {/* Anket Ekle/Düzenle Modal */}
      {modal&&(
        <Modal title={modal==='ekle'?'Yeni Anket Şablonu':`Düzenle: ${modal.baslik}`} onClose={()=>setModal(null)} wide>
          <form onSubmit={handleSave}>
            <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:14}}>
              {user.rol==='superadmin'&&modal==='ekle'&&(
                <div className="input-group">
                  <label className="input-label">Kurum *</label>
                  <select className="input" required value={form.kurum_id} onChange={e=>setForm({...form,kurum_id:e.target.value})}>
                    <option value="">Seçin...</option>
                    {kurumlar.map(k=><option key={k.id} value={k.id}>{k.ad}</option>)}
                  </select>
                </div>
              )}
              <div className="input-group">
                <label className="input-label">Anket Adı *</label>
                <input className="input" required value={form.baslik} onChange={e=>setForm({...form,baslik:e.target.value})} placeholder="Müşteri Memnuniyet Anketi"/>
              </div>
              <div className="input-group">
                <label className="input-label">Açıklama</label>
                <textarea className="input" rows={2} value={form.aciklama} onChange={e=>setForm({...form,aciklama:e.target.value})} style={{resize:'vertical'}}/>
              </div>
              <div>
                <div style={{fontWeight:600,fontSize:14,marginBottom:8,color:'var(--navy)'}}>
                  Sorular ({modal==='ekle'?form.sorular.length:modalSorular.length})
                </div>
                {modal==='ekle'?renderSorular(form.sorular,null,'form'):renderSorular(modalSorular,modal.id,'db')}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={()=>setModal(null)}>İptal</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving?<div className="spinner"/>:null} Kaydet
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Lokasyon Ata Modal */}
      {ataModal&&(
        <LokasyonAtaModal
          sablon={ataModal}
          tip="anket"
          onClose={()=>setAtaModal(null)}
          onAtandi={load}
        />
      )}
      {onizleme&&(
        <OnizlemeModal
          tip="anket"
          sablon={onizleme}
          onClose={()=>setOnizleme(null)}
        />
      )}
    </div>
  );
}